import React from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { TopNav, TopNavEnd, TopNavStart } from '@atlaskit/navigation-system/layout/top-nav';
import { AppLogo } from '@atlaskit/navigation-system/top-nav-items';
import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';

import { TwgLogoIcon } from './TwgLogoIcon';

const styles = cssMap({
	start: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.200'),
	},
	links: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
	},
	item: {
		minHeight: '40px',
		display: 'flex',
		alignItems: 'center',
		borderRadius: token('radius.medium'),
		color: token('color.text.subtle'),
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		textDecoration: 'none',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
			color: token('color.text'),
			textDecoration: 'none',
		},
	},
	selectedItem: {
		backgroundColor: token('color.background.selected'),
		color: token('color.text.selected'),
	},
});

export type QueryWorkbenchNavigationItem = {
	id: string;
	label: string;
	href: string;
	isSelected: boolean;
};

type QueryWorkbenchTopNavigationProps = {
	homeHref: string;
	homeLabel: string;
	items: readonly QueryWorkbenchNavigationItem[];
	onNavigate: (path: string) => void;
	endContent: React.ReactNode;
	navigationLabel: string;
};

export const QueryWorkbenchTopNavigation = ({
	homeHref,
	homeLabel,
	items,
	onNavigate,
	endContent,
	navigationLabel,
}: QueryWorkbenchTopNavigationProps): React.JSX.Element => (
	<TopNav height={70}>
		<TopNavStart sideNavToggleButton={null}>
			<Box xcss={styles.start}>
				<AppLogo
					href={homeHref}
					icon={TwgLogoIcon}
					name="Atlassian Teamwork Graph"
					label={homeLabel}
					onClick={(event) => {
						event.preventDefault();
						onNavigate(homeHref);
					}}
				/>
				<Box as="nav" xcss={styles.links} aria-label={navigationLabel}>
					{items.map((item) => (
						<Anchor
							key={item.id}
							href={item.href}
							aria-current={item.isSelected ? 'page' : undefined}
							xcss={cx(styles.item, item.isSelected && styles.selectedItem)}
							onClick={(event) => {
								if (
									event.button !== 0 ||
									event.metaKey ||
									event.ctrlKey ||
									event.shiftKey ||
									event.altKey
								) {
									return;
								}
								event.preventDefault();
								onNavigate(item.href);
							}}
						>
							{item.label}
						</Anchor>
					))}
				</Box>
			</Box>
		</TopNavStart>
		<TopNavEnd>{endContent}</TopNavEnd>
	</TopNav>
);
